/**
 * AuditFindingsTab -- Findings tab for audit detail view
 *
 * Fetches real findings data from Convex via useFindingsByAudit,
 * groups by category, and provides filter/search controls.
 */

import { useState, useMemo } from "react";
import { useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import { useFindingsByAudit } from "@/packages/features-shared";
import type { Id } from "@/packages/types";
import type {
  SeverityType,
  FindingStatusType,
} from "@/packages/domains/findings";
import { FindingsHeatmapStrip } from "./FindingsHeatmapStrip";
import { FindingsFilterBar } from "./FindingsFilterBar";
import { FindingsTimeline } from "./FindingsTimeline";
import { FindingsEmptyState } from "./FindingsEmptyState";

// ─── Types ───────────────────────────────────────────────────────────────────

interface AuditFindingsTabProps {
  auditId: Id<"audits">;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function AuditFindingsTab({ auditId }: AuditFindingsTabProps) {
  const findings = useFindingsByAudit(auditId);
  const categories = useQuery(api.checklist_queries.getCategoriesByAuditId, {
    auditId,
  });

  const [statusFilter, setStatusFilter] = useState<FindingStatusType | "all">(
    "all",
  );
  const [severityFilter, setSeverityFilter] = useState<SeverityType | "all">(
    "all",
  );
  const [searchText, setSearchText] = useState("");

  // Build category id -> name map
  const categoryMap = useMemo(() => {
    const map = new Map<string, string>();
    if (categories) {
      for (const cat of categories) {
        map.set(cat._id, cat.name);
      }
    }
    return map;
  }, [categories]);

  const filtered = useMemo(() => {
    if (!findings) return [];
    return findings.filter((f) => {
      if (statusFilter !== "all" && f.status !== statusFilter) return false;
      if (severityFilter !== "all" && f.severity !== severityFilter)
        return false;
      if (
        searchText &&
        !f.title.toLowerCase().includes(searchText.toLowerCase())
      )
        return false;
      return true;
    });
  }, [findings, statusFilter, severityFilter, searchText]);

  if (findings === undefined) {
    return (
      <div className="flex items-center justify-center py-16">
        <span className="text-sm text-muted-foreground">
          Loading findings...
        </span>
      </div>
    );
  }

  if (findings.length === 0) {
    return <FindingsEmptyState />;
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header: count */}
      {/* <div className="shrink-0 pb-3 flex items-center justify-between">
        <span className="text-xs text-secondary">
          {filtered.length} of {findings.length} finding
          {findings.length !== 1 && "s"}
        </span>
      </div> */}

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin pr-1">
        <FindingsHeatmapStrip findings={findings} />

        <FindingsFilterBar
          statusFilter={statusFilter}
          severityFilter={severityFilter}
          searchText={searchText}
          onStatusChange={setStatusFilter}
          onSeverityChange={setSeverityFilter}
          onSearchChange={setSearchText}
        />

        <FindingsTimeline findings={filtered} categoryMap={categoryMap} />
      </div>
    </div>
  );
}
